/**
 * WalletPage — full-screen overlay showing credit balance, top-up, transaction history.
 *
 * A15-N7. Matches settingsView pattern (ProfilePage, SubscriptionPage, etc.)
 *
 * Props:
 *  - onClose: () => void
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCredits } from '../../hooks/useCredits';
import TopUpModal from './TopUpModal';
import TransactionHistoryTable from './TransactionHistoryTable';

export default function WalletPage({ onClose }) {
  const { t } = useTranslation();
  const { balance, balanceEur, loading, error, recentTransactions, refetch } = useCredits();
  const [topUpOpen, setTopUpOpen] = useState(false);

  async function handleTopUpClose() {
    setTopUpOpen(false);
    await refetch();
  }

  return (
    <div
      className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col animate-slideInRight"
      data-testid="wallet-page"
    >
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]"
          data-testid="wallet-page-back"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <span className="text-sm font-semibold text-[var(--aif-text-primary)]">
          {t('credits.wallet_title', 'Mein Wallet')}
        </span>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">

          {/* Balance Card */}
          <div
            className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-6"
            data-testid="wallet-balance-card"
          >
            {loading ? (
              <div className="flex items-center justify-center h-20">
                <div className="w-6 h-6 border-2 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
              </div>
            ) : error ? (
              <div className="text-[var(--aif-color-danger)] text-sm">{error}</div>
            ) : (
              <>
                <div className="text-xs text-[var(--aif-text-muted)] mb-2">
                  {t('credits.current_balance', 'Aktuelles Guthaben')}
                </div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <div className="text-4xl font-bold text-[var(--aif-text-primary)]" data-testid="wallet-balance-credits">
                    {balance !== null ? balance.toLocaleString('de-DE') : '0'}
                  </div>
                  <div className="text-lg text-[var(--aif-text-secondary)]">Credits</div>
                  <div className="text-sm text-[var(--aif-text-muted)]" data-testid="wallet-balance-eur">
                    (≈ €{balanceEur?.toFixed(2) || '0.00'})
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => setTopUpOpen(true)}
                    className="bg-[var(--aif-color-primary)] hover:opacity-90 text-white px-6 py-2.5 rounded-xl font-medium text-sm transition aif-touch-target"
                    data-testid="wallet-topup-button"
                  >
                    {t('credits.topup_cta', '+ Credits aufladen')}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Info Card */}
          <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-4">
            <h2 className="text-xs font-semibold text-[var(--aif-text-secondary)] mb-2">
              {t('credits.what_are_credits', 'Was sind Credits?')}
            </h2>
            <p className="text-xs text-[var(--aif-text-muted)] leading-relaxed">
              {t('credits.credits_explanation',
                'Credits werden verbraucht, wenn Sie über Ihr Tageslimit hinaus KI-Funktionen nutzen. Innerhalb Ihres Abo-Pakets sind Anfragen kostenlos.'
              )}
            </p>
          </div>

          {/* Transaction History */}
          <div>
            <h2 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-3">
              {t('credits.transaction_history', 'Transaktionshistorie')}
            </h2>
            <TransactionHistoryTable />
          </div>
        </div>
      </div>

      {/* Top-Up Modal */}
      {topUpOpen && (
        <TopUpModal
          open={topUpOpen}
          onClose={handleTopUpClose}
          reason={null}
        />
      )}
    </div>
  );
}
